/**
 * GuestLanding
 * Landing page shown when a guest scans the QR code.
 * Collects: name, company, searchQuestion, @mention of inviting member.
 * Shows a QR code generator for the host/organizer (via qrcode package).
 *
 * Rule 6: < 300 lines.
 * Rule 7: 44px+ touch targets, mobile-first.
 * Rule 9: Strings from meetingConstants.
 * Rule 10: Loading + error state via GuestEntryState.
 */

import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'qrcode';
import { useGuestEntry } from '../hooks/useGuestEntry';
import {
  QR_LANDING_BASE_PATH,
  STRINGS,
} from '../meetingConstants';
import type { GuestSubmitPayload } from '../networkClubTypes';

// ── QR Code Generator (host view) ─────────────────────────────────────────
interface QrDisplayProps {
  entityId: string;
  roomName: string;
  accentColor?: string;
}

export const QrDisplay: React.FC<QrDisplayProps> = ({ entityId, roomName, accentColor = '#f97316' }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [qrError, setQrError] = useState<string | null>(null);

  const guestUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${QR_LANDING_BASE_PATH}/${entityId}/${roomName}/guest`;

  useEffect(() => {
    if (!canvasRef.current) return;
    QRCode.toCanvas(canvasRef.current, guestUrl, {
      width: 200,
      margin: 2,
      color: { dark: '#0f172a', light: '#ffffff' },
    }).catch((err) => {
      console.error('[QrDisplay] QR generate error:', err);
      setQrError('QR-code kon niet worden gegenereerd.');
    });
  }, [guestUrl]);

  return (
    <div className="flex flex-col items-center gap-2">
      {qrError ? (
        <p role="alert" className="text-xs text-rose-400">{qrError}</p>
      ) : (
        <canvas
          ref={canvasRef}
          className="rounded-xl border-4"
          style={{ borderColor: accentColor }}
          aria-label="QR-code voor gasten"
        />
      )}
      <p className="text-[10px] text-white/50 break-all max-w-[200px] text-center">{guestUrl}</p>
    </div>
  );
};

// ── Guest Landing Form (guest view) ───────────────────────────────────────
interface GuestLandingProps {
  entityId: string;
  roomName: string;
  clubName?: string;
  accentColor?: string;
}

const GuestLanding: React.FC<GuestLandingProps> = ({
  entityId,
  roomName,
  clubName,
  accentColor = '#f97316',
}) => {
  const { state, submit, reset } = useGuestEntry();
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [searchQuestion, setSearchQuestion] = useState('');
  const [mention, setMention] = useState('');

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    const payload: GuestSubmitPayload = {
      name,
      company,
      searchQuestion,
      invitedByMention: mention,
      entityId,
      roomName,
    };
    void submit(payload);
  };

  if (state.status === 'success') {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center px-6 text-white text-center"
        style={{ background: `linear-gradient(135deg, #0f172a 0%, ${accentColor}44 100%)` }}
      >
        <div className="text-5xl mb-4">🎉</div>
        <h1 className="text-2xl font-black mb-2">{STRINGS.guest.successHeading}</h1>
        <p className="text-base text-white/80 mb-6">
          {STRINGS.guest.successSub}
        </p>
        <p className="text-sm text-white/60">
          Je bent toegevoegd aan de bijeenkomst van {clubName ?? 'de netwerkclub'}.
        </p>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-6 py-12"
      style={{ background: `linear-gradient(135deg, #0f172a 0%, ${accentColor}44 100%)` }}
    >
      <div className="w-full max-w-sm space-y-6">
        {/* Header */}
        <div className="text-center text-white">
          {clubName && (
            <p className="text-xs uppercase tracking-widest text-white/50 mb-2">{clubName}</p>
          )}
          <h1 className="text-2xl font-black">{STRINGS.guest.heading}</h1>
          <p className="text-sm text-white/70 mt-1">{STRINGS.guest.sub}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-3" noValidate>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={STRINGS.guest.namePlaceholder}
            required
            autoComplete="name"
            aria-label={STRINGS.guest.namePlaceholder}
            className="w-full min-h-[48px] px-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
          />
          <input
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder={STRINGS.guest.companyPlaceholder}
            autoComplete="organization"
            aria-label={STRINGS.guest.companyPlaceholder}
            className="w-full min-h-[48px] px-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
          />
          <input
            value={searchQuestion}
            onChange={(e) => setSearchQuestion(e.target.value)}
            placeholder={STRINGS.guest.searchPlaceholder}
            aria-label={STRINGS.guest.searchPlaceholder}
            className="w-full min-h-[48px] px-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
          />
          <input
            value={mention}
            onChange={(e) => setMention(e.target.value)}
            placeholder={STRINGS.guest.mentionPlaceholder}
            aria-label={STRINGS.guest.mentionPlaceholder}
            className="w-full min-h-[48px] px-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-white/45 focus:outline-none focus:ring-2 focus:ring-white/40 text-sm"
          />

          {state.status === 'error' && (
            <p role="alert" className="text-sm text-rose-400 text-center">
              {state.message}
            </p>
          )}

          <button
            type="submit"
            disabled={state.status === 'submitting' || !name.trim()}
            aria-busy={state.status === 'submitting'}
            className="w-full min-h-[52px] rounded-2xl text-base font-black text-white transition-all disabled:opacity-50 active:scale-95"
            style={{ backgroundColor: accentColor }}
          >
            {state.status === 'submitting' ? 'Even geduld…' : STRINGS.guest.ctaLabel}
          </button>
        </form>
      </div>
    </div>
  );
};

export default GuestLanding;
